import React, { useState } from 'react';
import { Button, Input, Select, Checkbox, Card, Modal, Toast } from '@/components';

/**
 * StyleGuide - Interactive showcase of all UI components
 * Not part of the main app, used for development and testing
 * Route: /style-guide (admin only)
 */
export const StyleGuide: React.FC = () => {
  const [activeSection, setActiveSection] = useState('buttons');
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; message: string } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handleShowToast = (type: 'success' | 'error' | 'info' | 'warning') => {
    setToast({ type, message: `This is a ${type} toast notification!` });
  };

  const handleSimulateLoading = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    if (!formData.email) {
      setFormErrors((prev) => ({ ...prev, email: 'Email is required' }));
    }
    if (!formData.password) {
      setFormErrors((prev) => ({ ...prev, password: 'Password is required' }));
    }

    if (formData.email && formData.password) {
      handleShowToast('success');
    }
  };

  const sections = {
    buttons: (
      <div className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold mb-4">Variants</h3>
          <div className="flex flex-wrap gap-4">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="success">Success</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="ghost">Ghost</Button>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Sizes</h3>
          <div className="flex flex-wrap gap-4 items-center">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">States</h3>
          <div className="flex flex-wrap gap-4">
            <Button disabled>Disabled</Button>
            <Button isLoading={isLoading} onClick={handleSimulateLoading}>
              {isLoading ? 'Loading...' : 'Click to Load'}
            </Button>
            <Button fullWidth>Full Width</Button>
          </div>
        </div>
      </div>
    ),

    inputs: (
      <div className="space-y-6 max-w-md">
        <Input label="Default Input" placeholder="Type something..." />

        <Input
          label="Input with Error"
          error="This field is required"
          value="invalid"
        />

        <Input
          label="Input with Hint"
          hint="Minimum 8 characters"
          type="password"
        />

        <Input
          label="Email Input"
          type="email"
          placeholder="admin@example.com"
        />

        <Select
          label="Status"
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'printing', label: 'Printing' },
            { value: 'completed', label: 'Completed' },
          ]}
        />

        <Checkbox label="Remember me" />
        <Checkbox label="I agree to terms" defaultChecked />
        <Checkbox label="Checkbox with Error" error="You must agree" />
      </div>
    ),

    cards: (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h3 className="text-lg font-semibold mb-2">Elevated Card</h3>
          <p className="text-gray-600">This is the default card with shadow and border.</p>
        </Card>

        <Card variant="outlined">
          <h3 className="text-lg font-semibold mb-2">Outlined Card</h3>
          <p className="text-gray-600">This card has a border only, no shadow.</p>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Orders</h3>
          <p className="text-3xl font-bold text-blue-600">1,234</p>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-gray-600 mb-1">Completed Today</h3>
          <p className="text-3xl font-bold text-green-600">42</p>
        </Card>
      </div>
    ),

    modals: (
      <div className="space-y-4">
        <Button onClick={() => setShowModal(true)}>Open Modal</Button>

        <Modal
          isOpen={showModal}
          title="Example Modal"
          onClose={() => setShowModal(false)}
          actions={[
            {
              label: 'Save',
              onClick: () => {
                handleShowToast('success');
                setShowModal(false);
              },
              variant: 'primary',
            },
            {
              label: 'Delete',
              onClick: () => {
                handleShowToast('info');
                setShowModal(false);
              },
              variant: 'danger',
            },
          ]}
        >
          <p className="text-gray-700">
            This is a modal dialog. Press <kbd className="bg-gray-200 px-2 py-1 rounded">ESC</kbd> or click outside to close.
          </p>
        </Modal>
      </div>
    ),

    forms: (
      <form onSubmit={handleFormSubmit} className="max-w-md space-y-4">
        <Input
          label="Email"
          type="email"
          placeholder="admin@example.com"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          error={formErrors.email}
          required
        />

        <Input
          label="Password"
          type="password"
          placeholder="••••••••"
          value={formData.password}
          onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
          error={formErrors.password}
          required
        />

        <Checkbox label="Remember me" />

        <Button type="submit" fullWidth>
          Sign In
        </Button>
      </form>
    ),

    notifications: (
      <div className="space-y-4">
        <p className="text-gray-700">Click buttons to see toast notifications:</p>
        <div className="flex flex-wrap gap-3">
          <Button variant="success" onClick={() => handleShowToast('success')}>
            Success
          </Button>
          <Button variant="danger" onClick={() => handleShowToast('error')}>
            Error
          </Button>
          <Button onClick={() => handleShowToast('info')}>
            Info
          </Button>
          <Button variant="secondary" onClick={() => handleShowToast('warning')}>
            Warning
          </Button>
        </div>
      </div>
    ),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-xl font-bold text-gray-900">Design System</h1>
          </div>

          <div className="flex flex-wrap gap-2 pb-4">
            {Object.keys(sections).map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  activeSection === section
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card>{sections[activeSection as keyof typeof sections]}</Card>
      </main>

      {/* Toast Notification */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
          duration={4000}
        />
      )}
    </div>
  );
};

export default StyleGuide;
